/**
 * Undo and redo for the order editors.
 *
 * Every edit on a work order, a material order or the Other Expenses list is
 * written to the database the moment it is made — there is no Save button, and
 * there should not be one. What that costs is the safety net: a row deleted by
 * a misplaced click was gone, so each delete had to stop and ask "are you
 * sure?", on every row, every time.
 *
 * Jonathon, 2026-08-13: "i don't want it prompting me if i'm sure each time i'm
 * trying to delete something. especially since we're building an undo and redo
 * button."
 *
 * That trade is the right way round. A confirm dialog interrupts the ninety-nine
 * deliberate deletes to catch the one mistake, and it does not help at all with
 * the mistakes people actually make here — a quantity typed into the wrong row,
 * a product swapped on the wrong line. Undo covers all of them.
 *
 * What is here is only the bookkeeping: what happened, in what order, and what
 * would put it back. Nothing in this file talks to a database — the web app
 * turns an `Operation` into a call. That is what makes the awkward part, which
 * is the ordering, testable.
 */

/** A row, as it would be written. */
export type Row = Record<string, unknown>;

/**
 * One edit, with enough of the old world to rebuild it.
 *
 * `label` is what the toast says — "Removed Drip Edge 1.5\"" — and it is
 * written when the edit happens, because by the time somebody presses Undo the
 * row may no longer exist to describe.
 */
export type Edit =
  | { kind: "insert"; table: string; id: string; row: Row; label: string }
  | {
      kind: "update";
      table: string;
      id: string;
      before: Row;
      after: Row;
      label: string;
    }
  | { kind: "delete"; table: string; id: string; row: Row; label: string };

/** A single database call: what to run to move one step in either direction. */
export type Operation =
  | { kind: "insert"; table: string; row: Row }
  | { kind: "update"; table: string; id: string; values: Row }
  | { kind: "delete"; table: string; id: string };

export interface History {
  readonly past: readonly Edit[];
  readonly future: readonly Edit[];
}

export const emptyHistory: History = { past: [], future: [] };

/**
 * How far back undo goes.
 *
 * Long enough to cover a session's worth of fiddling with one order, short
 * enough that the deleted rows being held onto cannot grow without bound. An
 * editor is closed and reopened many times a day, and closing it forgets
 * everything anyway.
 */
export const HISTORY_LIMIT = 50;

/**
 * Note an edit that has just happened.
 *
 * Doing something new abandons the redo branch, which is what every editor in
 * the world does and what people expect: undo three times, type something, and
 * the three things you undid are not coming back.
 */
export function record(history: History, edit: Edit): History {
  const past = [...history.past, edit];
  return {
    past:
      past.length > HISTORY_LIMIT
        ? past.slice(past.length - HISTORY_LIMIT)
        : past,
    future: [],
  };
}

export const canUndo = (history: History): boolean => history.past.length > 0;
export const canRedo = (history: History): boolean => history.future.length > 0;

/** What the next undo would say it is undoing, for the button's tooltip. */
export const nextUndoLabel = (history: History): string | null =>
  history.past[history.past.length - 1]?.label ?? null;

export const nextRedoLabel = (history: History): string | null =>
  history.future[history.future.length - 1]?.label ?? null;

/**
 * Step back one edit.
 *
 * Returns the operation to run and the history as it will be once that
 * operation succeeds. Null when there is nothing to undo.
 */
export function undo(
  history: History,
): { history: History; operation: Operation; edit: Edit } | null {
  const edit = history.past[history.past.length - 1];
  if (!edit) return null;
  return {
    history: {
      past: history.past.slice(0, -1),
      future: [...history.future, edit],
    },
    operation: undoOperation(edit),
    edit,
  };
}

/** Step forward again. */
export function redo(
  history: History,
): { history: History; operation: Operation; edit: Edit } | null {
  const edit = history.future[history.future.length - 1];
  if (!edit) return null;
  return {
    history: {
      past: [...history.past, edit],
      future: history.future.slice(0, -1),
    },
    operation: redoOperation(edit),
    edit,
  };
}

/**
 * What undoes an edit.
 *
 * A deleted row is put back with the id it had, not a new one. That matters
 * more than it looks: the estimate's totals, the reconcile screen and the
 * printed order all reference these rows, and a "restored" line with a fresh id
 * is a different line that happens to read the same.
 */
export function undoOperation(edit: Edit): Operation {
  switch (edit.kind) {
    case "insert":
      return { kind: "delete", table: edit.table, id: edit.id };
    case "update":
      return {
        kind: "update",
        table: edit.table,
        id: edit.id,
        values: edit.before,
      };
    case "delete":
      return { kind: "insert", table: edit.table, row: edit.row };
  }
}

/** What redoes it. */
export function redoOperation(edit: Edit): Operation {
  switch (edit.kind) {
    case "insert":
      return { kind: "insert", table: edit.table, row: edit.row };
    case "update":
      return {
        kind: "update",
        table: edit.table,
        id: edit.id,
        values: edit.after,
      };
    case "delete":
      return { kind: "delete", table: edit.table, id: edit.id };
  }
}

/**
 * The old values behind a patch, taken off the row as it stands.
 *
 * Only the columns being written, so undoing a quantity change puts the
 * quantity back and touches nothing else — two people editing the same order
 * do not overwrite each other's untouched columns.
 */
export function valuesBefore(row: Row, patch: Row): Row {
  const before: Row = {};
  for (const key of Object.keys(patch)) before[key] = row[key] ?? null;
  return before;
}

/**
 * What a line item should be called in a toast.
 *
 * Material order items carry `name`, work order items carry `description`, and
 * an expense carries `label`. A row with none of them is a blank line somebody
 * added and then removed, and "Removed a line" is the honest thing to say.
 */
export function describeRow(row: Row): string {
  for (const key of ["name", "description", "label"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return "a line";
}
